import { formatParisDateTime, type OrderDetail } from '@flux/shared';
import { CheckCircle2, ExternalLink, OctagonAlert, SendHorizontal } from 'lucide-react';
import type { JSX } from 'react';
import { Button } from '@/components/ui/button.js';
import { OrderActionDialogs } from './order-action-dialogs.js';
import { orderActionTargetFromDetail, useOrderActionController } from './use-order-action-controller.js';

function shopifyCustomerUrl(order: OrderDetail): string | null {
  if (!order.customerId) {
    return null;
  }
  try {
    return `${new URL(order.shopifyAdminUrl).origin}/admin/customers/${order.customerId}`;
  } catch {
    return null;
  }
}

/**
 * Commande bloquée ou en erreur dont la dernière « Mise à jour depuis
 * Shopify » ne trouve plus de problème : il ne reste qu'à la renvoyer.
 * L'aperçu d'une préparation (`source: 'preparation'`) ne compte pas — une
 * commande en erreur d'envoi a pu être préparée sans encombre.
 */
export function isFixedByShopifyRefresh(order: OrderDetail): boolean {
  return (
    (order.status === 'blocked' || order.status === 'error') &&
    order.preview?.source === 'refresh' &&
    order.preview.decision === 'prepared'
  );
}

/**
 * Encart vert qui remplace l'encart rouge une fois le problème corrigé à la
 * source : l'action qui débloque la commande est dans l'encart lui-même.
 */
function OrderFixedBanner({ order }: { order: OrderDetail }): JSX.Element {
  const controller = useOrderActionController(orderActionTargetFromDetail(order));
  const previousReason =
    order.warnings.find((warning) => warning.code === 'order.blocked')?.message ?? order.statusReason;

  return (
    <>
      <div
        role="status"
        className="flex flex-col gap-3 rounded-lg border border-status-green-border bg-status-green-bg px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex min-w-0 gap-3">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-status-green-fg" aria-hidden="true" />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="font-semibold text-status-green-fg">Problème corrigé dans Shopify</p>
            <p className="break-words text-foreground">
              La mise à jour du{' '}
              {order.preview ? formatParisDateTime(new Date(order.preview.calculatedAt)) : '—'} ne trouve plus
              d’erreur. Renvoyez la commande à Martelet pour la débloquer.
            </p>
            {previousReason ? (
              <p className="break-words text-muted-foreground">Problème initial : {previousReason}</p>
            ) : null}
          </div>
        </div>
        <Button
          type="button"
          className="shrink-0 self-start sm:self-center"
          disabled={controller.isBusy}
          onClick={() => {
            controller.trigger('regenerate');
          }}
        >
          <SendHorizontal aria-hidden="true" />
          Renvoyer la commande à Martelet
        </Button>
      </div>
      <OrderActionDialogs controller={controller} />
    </>
  );
}

/**
 * Encart rouge « commande bloquée » (`02` §4) : motif exact renvoyé par le
 * pipeline (`order.statusReason`, `01` §2.2.a et §2.2.k). Pour un mojibake, le
 * champ et l'extrait fautif viennent de l'avertissement `order.blocked`
 * (`order.warnings`, message déjà formé côté serveur — jamais reformulé ici,
 * `02` §0), avec un lien vers la fiche client Shopify quand `customerId` est
 * connu.
 */
export function OrderBlockedBanner({ order }: { order: OrderDetail }): JSX.Element | null {
  if (isFixedByShopifyRefresh(order)) {
    return <OrderFixedBanner order={order} />;
  }
  if (order.status !== 'blocked') {
    return null;
  }

  const mojibakeWarning = order.warnings.find((warning) => warning.code === 'order.blocked');
  const customerUrl = shopifyCustomerUrl(order);

  return (
    <div
      role="alert"
      className="flex gap-3 rounded-lg border border-status-red-border bg-status-red-bg px-4 py-3 text-sm"
    >
      <OctagonAlert className="mt-0.5 size-4 shrink-0 text-status-red-fg" aria-hidden="true" />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-semibold text-status-red-fg">
          Commande bloquée — traitement manuel requis
        </p>
        <p className="break-words text-foreground">
          {mojibakeWarning?.message ?? order.statusReason ?? 'Motif non renseigné.'}
        </p>
        {mojibakeWarning?.field ? (
          <p className="text-foreground">
            Champ concerné :{' '}
            <span className="font-mono text-xs font-medium">{mojibakeWarning.field}</span>
          </p>
        ) : null}
        {order.preview?.source === 'refresh' ? (
          <p className="font-medium text-foreground">
            Toujours bloquée après la mise à jour du {formatParisDateTime(new Date(order.preview.calculatedAt))}{' '}
            depuis Shopify.
          </p>
        ) : null}
        <p className="text-muted-foreground">
          Corrigez la commande dans Shopify puis « Mettre à jour depuis Shopify » pour vérifier. Ou traitez-la
          manuellement chez Martelet puis « Marquer traitée ».
        </p>
        {customerUrl ? (
          <a
            href={customerUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-sm font-medium text-status-red-fg underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Voir la fiche client Shopify
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </div>
  );
}
